import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ConfigContext } from '../configuration/ConfigContext';
import OverlayConfirmModal from '../modal/OverlayConfirmModal';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { useNavigationPaths } from '../propertyAssociation/useNavigationPaths';
import { ToastContext } from '../toaster/ToastContext';
import { isDraft } from './assetHelpers';
import { deleteAsset, refreshAssets, useAsset } from './assetService';
import { DEFAULTS_EMPTY, DefaultsLookup } from './assetTypes/assetTypes';
import AssetTab from './components/AssetTab';
import NameModal from './modals/NameModal';
import AssetNotifications from './steps/AssetNotifications';
import AssetPreview from './steps/AssetPreview';
import AssetPublished from './steps/AssetPublished';
import AssetReadyToPublish from './steps/AssetReadyToPublish';
import AssetSchedule from './steps/AssetSchedule';

const steps = [
    { title: 'Översikt', availableIfPublished: true },
    { title: 'Bokningsbara tider', availableIfPublished: true },
    { title: 'Notiser', availableIfPublished: true },
    { title: 'Publicera', availableIfPublished: false },
];

const EditAsset: FunctionComponent = () => {
    const paths = useNavigationPaths();
    const navigate = useNavigate();
    const { id } = useParams() as any;
    const { addToast } = useContext(ToastContext);
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();
    const { asset, refetch } = useAsset(id);
    const [activeStep, setActiveStep] = useState(0);
    const [defaults, setDefaults] = useState(DEFAULTS_EMPTY);
    const [savedAt, setSavedAt] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNameModal, setShowNameModal] = useState(false);

    useEffect(() => {
        // Reset states when id changes
        setActiveStep(0);
        setDefaults(DEFAULTS_EMPTY);
        setSavedAt('');
        setShowDeleteModal(false);
        setShowNameModal(false);
    }, [id]);

    useEffect(() => {
        if (asset) {
            const newDefaults = DefaultsLookup[asset.assetType.tag];
            setDefaults(newDefaults ?? DEFAULTS_EMPTY);
        }
    }, [asset]);

    const updateValues = () => {
        refetch();
        setSavedAt(new Date().toLocaleTimeString());
    };

    const deleteDraft = () => {
        if (!asset) {
            gotoAssets();
            return;
        }

        deleteAsset(
            apiBaseUrl,
            currentPropertyAssociationId,
            asset.assetId,
            () => {
                addToast('Utkastet är nu borttaget');
                refreshAssets(apiBaseUrl, currentPropertyAssociationId, currentArea?.propertyAssociationAreaId);
                gotoAssets();
            },
            gotoAssets,
        );
    };

    const gotoNext = () => {
        setActiveStep(activeStep + 1);
    };

    const gotoPrevious = () => {
        setActiveStep(activeStep - 1);
    };

    const gotoAssets = () => {
        navigate(paths.assets);
    };

    if (!asset || !defaults) {
        return null;
    }

    const renderCancelButton = () => {
        if (isDraft(asset.status)) {
            return (
                <div className="paper__secondary-buttons">
                    <button className="btn btn-lg btn-secondary" onClick={() => setShowDeleteModal(true)}>
                        Avbryt
                    </button>
                </div>
            );
        }

        return <div className="paper__secondary-buttons"></div>;
    };

    const actions = {
        gotoNext,
        gotoPrevious,
        renderCancelButton,
        updateValues,
    };
    const assetIsDraft = isDraft(asset.status);

    return (
        <div className="app__inner app__inner--paper paper">
            <div className="paper__header">
                <h1 className="paper__heading">
                    <button className="edit-this" onClick={() => setShowNameModal(true)}>
                        {asset.name}
                    </button>
                </h1>
                <button className="paper__close" onClick={gotoAssets}>
                    Stäng
                </button>
                {savedAt && <div className="paper__status">Senast sparad {savedAt}</div>}
                <div className="paper__tabs">
                    {steps.map(
                        (x, index) =>
                            (assetIsDraft || x.availableIfPublished) && (
                                <AssetTab key={index} activeStep={activeStep} index={index} assetStatus={asset.status} setActiveStep={setActiveStep} title={x.title} />
                            ),
                    )}
                </div>
            </div>

            {activeStep === 0 && <AssetPreview defaults={defaults} asset={asset} {...actions} />}
            {activeStep === 1 && <AssetSchedule defaults={defaults} asset={asset} {...actions} />}
            {activeStep === 2 && <AssetNotifications asset={asset} {...actions} />}
            {activeStep === 3 && <AssetReadyToPublish asset={asset} {...actions} />}
            {activeStep === 4 && <AssetPublished />}

            <NameModal initialValues={asset} defaults={defaults} updateValues={updateValues} visible={showNameModal} onClose={() => setShowNameModal(false)} />
            <OverlayConfirmModal
                heading="Ta bort utkast?"
                text="Vill du ta bort utkastet? En borttagen resurs går ej att återställa."
                okButtonText="Ta bort utkast"
                cancelButtonText="Avbryt"
                visible={showDeleteModal}
                onSubmit={deleteDraft}
                onCancel={gotoAssets}
            />
        </div>
    );
};

export default EditAsset;
